/**
 * Truncate a markdown value for readonly UI display.
 *
 * If the truncation was necessary, an ellipsis will offer the full content in a tooltip.
 */

import {
  TEAL_500,
  TEAL_600,
  THEME,
} from '@wandb/weave/common/css/globals.styles';
import {
  generateHTML,
  markdownToText,
  sanitizeHTML,
} from '@wandb/weave/common/util/markdown';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React from 'react';
import styled from 'styled-components';

type TooltipPosition =
  | 'top left'
  | 'top right'
  | 'bottom right'
  | 'bottom left'
  | 'right center'
  | 'left center'
  | 'top center'
  | 'bottom center';

type TruncatedMarkdownProps = {
  content: string;
  maxLength: number;
  firstLineOnly?: boolean;
  tooltipPosition?: TooltipPosition;
};

// TODO: Make this smarter, don't cut off mid-word.
export function truncate(
  content: string,
  maxLength: number,
  firstLineOnly: boolean
): string {
  let truncated = content.substring(0, maxLength);
  if (firstLineOnly) {
    truncated = truncated.split('\n', 1)[0];
  }
  return truncated;
}

export const Ellipsis = styled.span`
  margin-left: 8px;
  font-weight: 600;
  color: light-dark(${TEAL_600}, ${THEME.textLink});
  cursor: default;
  &:hover {
    color: light-dark(${TEAL_500}, ${THEME.accentHover});
  }
`;
Ellipsis.displayName = 'S.Ellipsis';

type MarkdownTooltipProps = {
  markdown: string;
  tooltipPosition?: TooltipPosition;
};

const MarkdownTooltip = ({markdown, tooltipPosition}: MarkdownTooltipProps) => {
  // I wanted to use the Markdown component, but it was doing some dynamic sizing
  // things (I didn't really investigate) that made it not work with the tooltip.
  const htmlStr = sanitizeHTML(generateHTML(markdown));
  const tooltipContent = (
    <div
      dangerouslySetInnerHTML={{
        __html: htmlStr,
      }}
    />
  );
  return (
    <Tooltip
      content={tooltipContent}
      trigger={<Ellipsis>…</Ellipsis>}
      position={tooltipPosition}
    />
  );
};

export const TruncatedMarkdown = ({
  content,
  maxLength,
  firstLineOnly = false,
  tooltipPosition,
}: TruncatedMarkdownProps) => {
  const trimmed = content.trim();
  const textContent = markdownToText(trimmed);
  const truncated = truncate(textContent, maxLength, firstLineOnly);
  return (
    <div>
      {truncated}
      {truncated !== trimmed && (
        <MarkdownTooltip markdown={trimmed} tooltipPosition={tooltipPosition} />
      )}
    </div>
  );
};
